import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, BedDouble, Bath, Maximize2, ShieldCheck, MapPin, Star } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80";

// Deterministic 90-98% "AI Match" score derived from the property id so it is
// stable across renders (marketing display for the featured hero card).
const aiMatchScore = (id = "") => {
  let h = 0;
  for (let i = 0; i < String(id).length; i += 1) h = (h * 31 + String(id).charCodeAt(i)) % 1000;
  return 90 + (h % 9); // 90..98
};

const fullPrice = (value, currency = "ETB") =>
  `${currency} ${Number(value || 0).toLocaleString("en-US")}`;

/**
 * Large split "hero" featured card matching the approved mockup:
 * image on the left, rich details on the right.
 */
export const FeaturedHeroCard = ({ property }) => {
  const p = property || {};
  const cover = (p.images || []).find((i) => i.is_cover) || (p.images || [])[0];
  const img = cover?.url || FALLBACK;
  const typeName = p.type?.name || p.property_type || null;
  const [fav, setFav] = useState(false);
  const score = aiMatchScore(p.id);

  return (
    <article
      data-testid="featured-hero-card"
      className="overflow-hidden rounded-[var(--ms-radius-lg)] border border-[hsl(var(--ms-border))] bg-[hsl(var(--ms-card))] shadow-[var(--ms-shadow-md)] transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[var(--ms-shadow-lg)]"
    >
      <div className="flex">
        {/* Image (left) */}
        <div className="relative w-[42%] shrink-0 sm:w-[40%] lg:w-[38%]">
          <Link to={`/properties/${p.id}`} className="block h-full">
            <img
              src={img}
              alt={p.title}
              loading="lazy"
              decoding="async"
              className="h-full min-h-[150px] w-full object-cover"
            />
          </Link>
          <span
            data-testid="featured-hero-badge"
            className="absolute left-2.5 top-2.5 rounded-full bg-[#10B981] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-[var(--shadow-sm)]"
          >
            Featured
          </span>
          <button
            type="button"
            onClick={() => setFav((v) => !v)}
            data-testid="featured-hero-favorite"
            aria-label="Save to favorites"
            className="ms-glass absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full text-slate-700 transition-transform duration-150 hover:scale-105"
          >
            <Heart className={`h-4 w-4 ${fav ? "fill-[#DC2626] text-[#DC2626]" : ""}`} />
          </button>
        </div>

        {/* Details (right) */}
        <div className="flex flex-1 flex-col justify-center gap-1.5 p-3.5 sm:p-5">
          <Link
            to={`/properties/${p.id}`}
            data-testid="featured-hero-title"
            className="line-clamp-1 text-base font-extrabold leading-snug text-[hsl(var(--ms-fg))] hover:text-[hsl(var(--ms-blue))] sm:text-xl"
          >
            {p.title || "Featured Property"}
          </Link>

          {p.verified && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[#0E9F6E] dark:text-[#34D399] sm:text-sm">
              <ShieldCheck className="h-4 w-4" /> Verified Property
            </span>
          )}

          <span className="flex items-center gap-1.5 text-xs text-[hsl(var(--ms-muted-fg))] sm:text-sm">
            <MapPin className="h-4 w-4 text-[hsl(var(--ms-blue))]" />
            <span className="line-clamp-1">{p.address || "Ethiopia"}</span>
          </span>

          <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[hsl(var(--ms-muted-fg))] sm:text-sm">
            <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4" /> {p.bedrooms ?? 0} Beds</span>
            <span className="flex items-center gap-1.5"><Bath className="h-4 w-4" /> {p.bathrooms ?? 0} Baths</span>
            {p.square_meters ? (
              <span className="flex items-center gap-1.5"><Maximize2 className="h-4 w-4" /> {p.square_meters} m²</span>
            ) : null}
          </div>

          <span className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#10B981]/12 px-2.5 py-1 text-xs font-semibold text-[#0E9F6E] dark:text-[#34D399]">
            <Star className="h-3.5 w-3.5 fill-current" /> AI Match {score}%
          </span>

          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5">
            <p
              data-testid="featured-hero-price"
              className="text-lg font-extrabold tabular-nums tracking-tight text-[hsl(var(--ms-blue))] sm:text-xl"
            >
              {fullPrice(p.price, p.currency)}
            </p>
            {typeName && (
              <span className="max-w-full shrink truncate rounded-full bg-[hsl(var(--ms-blue))]/10 px-3 py-1 text-xs font-semibold text-[hsl(var(--ms-blue))]">
                {typeName}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default FeaturedHeroCard;
